import React, { useRef } from 'react';
import { Box } from '@mui/material';

import { useTranslation } from 'react-i18next';

import CartLoader from '../../../Helper/Loaders/CartLoader';

import BaseVitalChart from '../../../PagesContent/DigitalTwinV3/components/BaseVitalChart';

import {
    getBaseCanvasSettings,
    getBaseChartSettings,
    getBaseTooltipSettings,
    getBaseXAxisSettings,
    isBaseDataSeriesEmpty
} from '../../../Helper/DigitalTwin/VitalChartConfigurations';

import {
    calculateDynamicRange,
    getTooltipHtml,
    swapIndexForMultiYAxis
} from '../../../Helper/DigitalTwin/DigitalTwinFunctions';

import alertIconStart from '../../../Assets/Icons/DigitalTwin/AlertAnnotationStart.svg';
import alertIconEnd from '../../../Assets/Icons/DigitalTwin/AlertAnnotationEnd.svg';
import alertIconExpired from '../../../Assets/Icons/DigitalTwin/AlertAnnotationExpired.svg';


const SingleAlertChart = ({
    series,
    categories,
    startDate,
    isLoading,
    resetToLatestDataDate = () => { },
    chartID = "chartID",
    metrics,
    primaryGranularity,
    currentAlert,
    visibleSeries,
    onZoom = () => {},
    onZoomReset = () => {},
    zoomedRange = null,
    setLoading = null,
    isZoomed = false
}) => {

    const zoomResetViewRef = useRef(null);

    const { t } = useTranslation()

    const getChartSettings = () => {
        return getBaseChartSettings(chartID, onZoom, setLoading)
    }

    const getYAxisSettings = () => {
        const yAxisSettings = [];
        const swapedSeries = swapIndexForMultiYAxis(series)
        for (let i in swapedSeries) {
            let rightAxis = i > 1;
            let seriesItem = swapedSeries[i];
            let metric = metrics.find((metric) => metric.key == seriesItem.key) || {}
            let ranges = metric && metric.key ? metric.ranges : seriesItem.ranges

            ranges = calculateDynamicRange(seriesItem.data, ranges.min, ranges.max)

            let offsetX = -35;

            if (i / 2 == 0) {
                offsetX = -60;
            }

            offsetX = -15;

            yAxisSettings.push({
                seriesName: seriesItem.name,
                opposite: rightAxis,
                showAlways: true,
                tickAmount: 5,
                position: 'top',
                min: ranges.min,
                max: ranges.max,
                axisTicks: {
                    show: false,
                    borderType: 'dotted'
                },
                axisBorder: {
                    show: true,
                    color: metric.color,
                    offsetY: 0,
                    offsetX: -7
                },
                title: {
                    text: seriesItem.unit, // Y Axis Label
                    position: 'high',
                    rotate: 0,
                    offsetX: rightAxis ? -30 : 30,
                    offsetY: -160,
                    style: {
                        fontSize: '12px',
                        fontWeight: 400,
                        color: '#686868',
                    },
                },
                labels: {
                    show: true,
                    offsetX: offsetX,
                    offsetY: 0,
                    style: {
                        colors: '#87939D',
                        fontSize: '10px',
                        fontWeight: 500,
                        fontfamily: 'Roboto !important',
                    },
                    formatter: (value) => Math.round(value),
                },
            })
        }

        return yAxisSettings;
    }

    const getXAxisSettings = () => {
        const settings = getBaseXAxisSettings(primaryGranularity, categories, startDate, zoomedRange)
        return settings
    }

    const getToolTipSettings = () => {
        const tooltipSettings = getBaseTooltipSettings()
        tooltipSettings.custom = (opts) => getTooltipHtml(opts, metrics, t, visibleSeries)
        return tooltipSettings
    }

    const getYAxisMax = () => {
        const yAxis = getYAxisSettings()[0]
        return yAxis ? yAxis.max : 20
    }

    const getOngoingAlertAnnotation = () => {
        const annotations = {}

        annotations.xaxis = [
            {
                // Start Annotation
                x: new Date(currentAlert.activatedAt).getTime(),
                borderColor: '#BBBBBB',
                strokeDashArray: 0,
            },
            {
                x: new Date(currentAlert.activatedAt).getTime(),
                x2: new Date().getTime(),
                fillColor: '#FFE4E4',
            }
        ]

        annotations.points = [
            {
                x: new Date(currentAlert.activatedAt).getTime(),
                y: getYAxisMax(),
                offsetY: 10,
                marker: {
                    size: 0,
                },
                image: {
                    path: alertIconStart,
                    width: 25,
                    height: 25,
                    offsetX: 0,
                    offsetY: 0
                }
            },
        ]

        return annotations
    }

    const getResolvedAlertAnnotation = () => {
        const annotations = {}

        annotations.xaxis = [
            {
                x: new Date(currentAlert.activatedAt).getTime(),
                borderColor: '#BBBBBB',
                strokeDashArray: 0,
            },
            {
                x: new Date(currentAlert.resolvedAt).getTime(),
                borderColor: '#BBBBBB',
                strokeDashArray: 0,
            },
            {
                x: new Date(currentAlert.activatedAt).getTime(),
                x2: new Date(currentAlert.resolvedAt).getTime(),
                fillColor: '#FFE4E4',
            }
        ]

        annotations.points = [
            {
                x: new Date(currentAlert.activatedAt).getTime(),
                y: getYAxisMax(),
                offsetY: 10,
                image: {
                    path: alertIconStart,
                    width: 25,
                    height: 25,
                    offsetX: 0,
                    offsetY: 0
                }
            },
            {
                x: new Date(currentAlert.resolvedAt).getTime(),
                y: getYAxisMax(),
                offsetY: 10,
                image: {
                    path: alertIconEnd,
                    width: 25,
                    height: 25,
                    offsetX: 0,
                    offsetY: 0
                }
            }
        ]

        return annotations
    }

    const getExpiredAlertAnnotation = () => {
        const annotations = {}

        if (currentAlert.resolvedAt) {
            return getResolvedAlertAnnotation()
        }

        annotations.xaxis = [
            {
                // Start Annotation
                x: new Date(currentAlert.activatedAt).getTime(),
                borderColor: '#BBBBBB',
                strokeDashArray: 0,
            },
            {
                x: new Date(currentAlert.activatedAt).getTime(),
                x2: new Date(currentAlert.expiredAt).getTime(),
                fillColor: '#FFE4E4',
            }
        ]

        annotations.points = [
            {
                x: new Date(currentAlert.activatedAt).getTime(),
                y: getYAxisMax(),
                offsetY: 10,
                marker: {
                    size: 0,
                },
                image: {
                    path: alertIconStart,
                    width: 25,
                    height: 25,
                    offsetX: 0,
                    offsetY: 0
                }
            },

            {
                x: new Date(currentAlert.expiredAt).getTime(),
                y: getYAxisMax(),
                offsetY: 10,
                image: {
                    path: alertIconExpired,
                    width: 25,
                    height: 25,
                    offsetX: 0,
                    offsetY: 0
                }
            }
        ]

        return annotations
    }

    const getAnnotations = () => {
        const annotations = {}
        if (['active', 'open'].includes(currentAlert.alertStatus.toLowerCase())) {
            return getOngoingAlertAnnotation()
        }

        if (['resolved'].includes(currentAlert.alertStatus.toLowerCase())) {
            return getResolvedAlertAnnotation()
        }

        if (['expired'].includes(currentAlert.alertStatus.toLowerCase())) {
            return getExpiredAlertAnnotation()
        }


        return annotations
    }

    const isDataSeriesEmpty = () => {
        return isBaseDataSeriesEmpty(series)
    }

    const getCanvasSettings = () => {
        return getBaseCanvasSettings(primaryGranularity, series)
    }

    const getChartOptions = () => {
        return {
            ...getCanvasSettings(),
            annotations: getAnnotations(),
            chart: getChartSettings(),
            xaxis: getXAxisSettings(),
            yaxis: getYAxisSettings(),
            tooltip: getToolTipSettings(),
        };
    }

    if (isLoading) {
        return (
            <Box sx={{ minHeight: '360px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CartLoader />
            </Box>
        );
    }

    const noDataMessage = currentAlert?.level?.toLowerCase() == 'battery' ? 'No chart available for the battery level alerts' : 'No data available for the selected date'

    return (
        <BaseVitalChart
            options={getChartOptions()}
            series={series}
            chartID={chartID}
            chartHeight={360}
            primaryGranularity={primaryGranularity}
            resetToLatestDataDate={resetToLatestDataDate}
            isDataSeriesEmpty={isDataSeriesEmpty}
            zoomResetViewRef={zoomResetViewRef}
            disableRedirect={true}
            noDataMessage={t(noDataMessage)}
            onZoomReset={onZoomReset}
            isZoomed={isZoomed}
        />
    );
};

export default SingleAlertChart;
